/**
 * A tile whose chart type the app does not draw (map, timeline, word cloud,
 * custom visualization): Looker's own picture of it, asked for once the card's
 * width is known. While Looker draws, the card says so; if it cannot, the
 * card shows the data instead. The data is always one click away, and it is
 * what the chat reads.
 *
 * @module components/looker/LookerTileImage
 */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LOOKER_RENDER_HEIGHT as RENDER_IMAGE_HEIGHT, type LookerTile } from '@/services/looker/lookerTypes';
import { fetchLookerTileImage, type LookerImageRequest } from '@/services/looker/lookerApi';
import { renderWidthFor } from './lookerChartData';

type State = { kind: 'pending' } | { kind: 'ok'; src: string; renderedAt: string } | { kind: 'failed' };

export const LookerTileImage: React.FC<{ tile: LookerTile; request: LookerImageRequest; fallback: React.ReactNode }> = ({
  tile, request, fallback,
}) => {
  const { t, i18n } = useTranslation();
  const box = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<State>({ kind: 'pending' });
  // Joined into one key so a new filters object with the same values does not re-render the picture.
  const key = JSON.stringify([request, tile.id]);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'pending' });
    const width = renderWidthFor(box.current?.clientWidth ?? 0);
    void fetchLookerTileImage({ ...request, tileId: tile.id, width }).then((r) => {
      if (cancelled) return;
      setState(r.ok ? { kind: 'ok', src: `data:image/png;base64,${r.data.image}`, renderedAt: r.data.renderedAt } : { kind: 'failed' });
    });
    return () => {
      cancelled = true;
    };
    // `key` carries request and tile id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  if (state.kind === 'failed') {
    return (
      <div>
        <p className="mb-2 text-xs text-neutral-700">{t('looker.renderFailed')}</p>
        {fallback}
      </div>
    );
  }

  const time = state.kind === 'ok'
    ? new Date(state.renderedAt).toLocaleTimeString(i18n.language === 'th' ? 'th-TH' : 'en-GB', { hour: '2-digit', minute: '2-digit' })
    : '';
  return (
    <div ref={box}>
      {state.kind === 'pending' ? (
        <div
          role="status"
          className="flex animate-pulse items-center justify-center rounded-xl bg-neutral-50 text-sm text-neutral-700"
          style={{ height: RENDER_IMAGE_HEIGHT }}
        >
          {t('looker.renderPending')}
        </div>
      ) : (
        <img src={state.src} alt={tile.title} className="w-full rounded-xl object-contain" style={{ maxHeight: RENDER_IMAGE_HEIGHT }} />
      )}
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        {state.kind === 'ok' && <p className="text-xs text-neutral-700">{t('looker.renderedBy', { time })}</p>}
        <details className="w-full text-sm">
          <summary className="cursor-pointer text-xs font-medium text-neutral-800">{t('looker.showData')}</summary>
          <div className="mt-2">{fallback}</div>
        </details>
      </div>
    </div>
  );
};
